import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import PageMeta from "../../../../shared/ui/common/PageMeta";
import PatientsList from "../components/PatientsList";
import CouplesList from "../components/CouplesList";
import GroupsList from "../components/GroupsList";
import Label from "../../../../shared/ui/form/Label";
import FilterSelect from "../../../../shared/ui/form/FilterSelect";
import Button from "../../../../shared/ui/button/Button";
import TabSwitcher from "../../../../shared/ui/tabs/TabSwitcher";
import { PageHeader, Surface } from "../../../../shared/ui/primitives";
import { useMotionSafe } from "../../../../hooks/useMotionSafe";
import { useTherapist } from "../../../../context/TherapistProvider";
import { useNavigate, useSearchParams } from "react-router";
import { History, Search, User, Users } from "lucide-react";
import { GroupOfPeopleIcon } from "../../../../shared/ui/icons/SubjectKindIcon";
import { useCenter, useCenterCourses, useSessionPreferences } from "../../../center";
import { useAcademicYearOptions } from "../components/StudentCourseFields";
import PastSessionsImport from "../components/PastSessionsImport";

type PatientTab = "individual" | "couple" | "group";
const TAB_IDS: PatientTab[] = ["individual", "couple", "group"];

const PANEL_ID = "patients-panel";
const SEARCH_ID = "patients-search";
/** How long typing must pause before the list is searched again. */
export const SEARCH_DEBOUNCE_MS = 300;

/** A course and its saved modules, as a past session's module is chosen from. */
const courseOption = (course: { id: string; internal_name: string; phases: { id?: string; name: string }[] }) => ({
  id: course.id,
  label: course.internal_name,
  modules: course.phases.flatMap((phase) => (phase.id ? [{ id: phase.id, label: phase.name }] : [])),
});

export default function Patients() {
  const { t } = useTranslation();
  const { reduced } = useMotionSafe();
  // The tab lives in the address, so coming back from a group lands on groups.
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab") as PatientTab | null;
  // The people are always listed; couples and groups only where the centre
  // runs them, so a stale `?tab=couple` lands on the people instead.
  const { modes } = useSessionPreferences();
  const tabIds = TAB_IDS.filter((id) => id === "individual" || modes[id]);
  const activeTab: PatientTab = requested && tabIds.includes(requested) ? requested : "individual";
  const setActiveTab = (tab: PatientTab) =>
    setSearchParams(
      (params) => {
        params.set("tab", tab);
        return params;
      },
      { replace: true },
    );
  // So does the search (`?q=`); the box holds what is typed until it settles.
  const query = searchParams.get("q") ?? "";
  const [searchText, setSearchText] = useState(query);
  const [filters, setFilters] = useState({
    courseId: "",
    academicYear: "",
  });
  // A training centre's students can be narrowed to a course and a year of it.
  const { center } = useCenter();
  const isTraining = center?.type === "training";
  const { courses } = useCenterCourses(center?.id, isTraining);
  const yearOptions = useAcademicYearOptions(filters.academicYear);

  const { therapist } = useTherapist();
  const navigate = useNavigate();
  // Sessions held before Alchely, brought in so the reports count them.
  const [importingHistory, setImportingHistory] = useState(false);

  const setQuery = (next: string) =>
    setSearchParams(
      (params) => {
        if (next) params.set("q", next);
        else params.delete("q");
        return params;
      },
      { replace: true },
    );

  // Searched on the server — name, surname and the centre's identifiers — so
  // nobody is missing because the caseload is large.
  useEffect(() => {
    const next = searchText.trim();
    if (next === query) return;
    const timer = setTimeout(() => setQuery(next), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // setQuery is rebuilt every render; the timer depends on the text and the address only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, query]);

  const handleClearFilters = () => {
    setSearchText("");
    setQuery("");
    setFilters({ courseId: "", academicYear: "" });
  };

  const listFilters = { name: query, ...filters };

  const tabs = [
    { id: "individual" as const, label: t("patient.individualTab"), icon: User },
    { id: "couple" as const, label: t("patient.coupleTab"), icon: Users },
    { id: "group" as const, label: t("patient.groupTab"), icon: GroupOfPeopleIcon },
  ].filter((tab) => tabIds.includes(tab.id));

  const isIndividual = activeTab === "individual";
  const tabLabel = tabs.find((tab) => tab.id === activeTab)?.label;

  return (
    <>
      <PageMeta title={t("patient.pageTitle")} />

      <div className="space-y-6">
        <PageHeader
          title={t("patient.pageTitle")}
          actions={
            isIndividual ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => setImportingHistory(true)}
                  startIcon={<History size={16} aria-hidden="true" />}
                >
                  {t("pastSessions.open")}
                </Button>
                <Button
                  data-tour="create-patient-btn"
                  onClick={() => navigate("/patients/new")}
                >
                  {t("patient.newPatient")}
                </Button>
              </div>
            ) : activeTab === "couple" ? (
              <Button onClick={() => navigate("/couple-patient/new")}>
                {t("patient.newCouple")}
              </Button>
            ) : (
              <Button onClick={() => navigate("/group-patient/new")}>
                {t("patient.newGroup")}
              </Button>
            )
          }
        />

        {/* Shared with five other screens rather than inlined a seventh time. */}
        <TabSwitcher
          tabs={tabs}
          activeTab={activeTab}
          onChange={setActiveTab}
          label={t("patient.typeTabsLabel")}
          layoutId="activeTabPatients"
          panelId={PANEL_ID}
        />

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            id={PANEL_ID}
            role="tabpanel"
            aria-label={tabLabel}
            initial={{ opacity: 0, y: reduced ? 0 : 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: reduced ? 0 : -6 }}
            transition={{ duration: reduced ? 0.12 : 0.2 }}
          >
            <Surface as="section">
              <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end">
                <div className="min-w-[200px] flex-1">
                  <Label htmlFor={SEARCH_ID}>{t("patient.search.label")}</Label>
                  <div className="relative">
                    <Search
                      aria-hidden="true"
                      className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-content-tertiary"
                    />
                    <input
                      id={SEARCH_ID}
                      type="search"
                      autoComplete="off"
                      className="field-base field-default h-[50px] rounded-xl py-3 pl-12 pr-4"
                      placeholder={
                        isIndividual
                          ? t("patient.search.placeholderIndividual")
                          : t("patient.search.placeholderOther")
                      }
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                  </div>
                </div>

                {isIndividual && isTraining && (
                  <>
                    <div className="min-w-[180px] flex-1">
                      <Label id="patients-filter-course-label">{t("patient.course.course")}</Label>
                      <FilterSelect
                        aria-labelledby="patients-filter-course-label"
                        options={courses.map((course) => ({ value: course.id, label: course.internal_name }))}
                        value={filters.courseId}
                        onChange={(val) =>
                          setFilters((prev) => ({ ...prev, courseId: (val as string) || "" }))
                        }
                        placeholder={t("patient.course.anyCourse")}
                        searchPlaceholder={t("patient.searchPlaceholder")}
                        className="border-border-strong bg-transparent text-sm"
                      />
                    </div>
                    <div className="min-w-[140px]">
                      <Label id="patients-filter-year-label">{t("patient.course.year")}</Label>
                      <FilterSelect
                        aria-labelledby="patients-filter-year-label"
                        options={yearOptions}
                        value={filters.academicYear}
                        onChange={(val) =>
                          setFilters((prev) => ({ ...prev, academicYear: (val as string) || "" }))
                        }
                        placeholder={t("patient.course.anyYear")}
                        searchPlaceholder={t("patient.searchPlaceholder")}
                        className="border-border-strong bg-transparent text-sm"
                      />
                    </div>
                  </>
                )}

                <Button variant="outline" onClick={handleClearFilters}>
                  {t("common.clear")}
                </Button>
              </div>

              {isIndividual ? (
                <PatientsList therapistID={therapist?.id ?? ""} filters={listFilters} />
              ) : activeTab === "couple" ? (
                <CouplesList therapistID={therapist?.id ?? ""} filters={listFilters} />
              ) : (
                <GroupsList filters={listFilters} />
              )}
            </Surface>
          </motion.div>
        </AnimatePresence>
      </div>
      {importingHistory && (
        <PastSessionsImport
          therapistId={therapist?.id}
          onClose={() => setImportingHistory(false)}
          courses={isTraining ? courses.map(courseOption) : undefined}
        />
      )}
    </>
  );
}
